// services/subscription.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export enum SubscriptionType {
  HOTEL = 'Hotel',
  RESTAURANT = 'Restaurant',
  TASK_MANAGEMENT = 'TaskManagement',
}

export interface FeatureAccess {
  // Level 1 Menus
  dashboard: boolean;
  messaging: boolean;
  offerings: boolean;
  inventoryManagement: boolean;
  hotelGuests: boolean;
  restaurants: boolean;
  settings: boolean;

  // Messaging - Level 2
  chats: boolean;
  chatbotConversation: boolean;
  reportedMessageRequests: boolean;
  reportedMessageLogs: boolean;

  // Offerings - Level 2
  rooms: boolean;
  services: boolean;
  restaurantOfferings: boolean;
  toursEventsPoi: boolean;
  hotelGuide: boolean;
  news: boolean;

  // Inventory Management - Level 2
  inventoryRequests: boolean;
  serviceInventoryLogs: boolean;
  restaurantInventoryLogs: boolean;

  // Hotel Guests - Level 2
  manageHotelGuests: boolean;
  manageHotelBookings: boolean;
  manageHotelGuestCheckInPreferences: boolean;
  hotelGuestCheckInRequests: boolean;
  hotelGuestCheckInRequestsLogs: boolean;
  hotelGuestPermissions: boolean;

  // Restaurants - Level 2
  manageRestaurantOrders: boolean;
  manageRestaurantGuests: boolean;

  // Settings - Level 2
  organization: boolean;
  homepage: boolean;
  languages: boolean;
  paymentMethods: boolean;
  aiAccount: boolean;
  manageEmployeeHandbooks: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private subscriptionType = new BehaviorSubject<string>('');
  public subscriptionType$ = this.subscriptionType.asObservable();

  // Define feature access for each subscription type
  private featureMatrix: Record<string, FeatureAccess> = {
    [SubscriptionType.HOTEL]: {
      dashboard: true,
      messaging: true,
      offerings: true,
      inventoryManagement: true,
      hotelGuests: true,
      restaurants: true,
      settings: true,

      // Messaging - Level 2
      chats: true,
      chatbotConversation: true,
      reportedMessageRequests: true,
      reportedMessageLogs: true,

      // Offerings - Level 2
      rooms: true,
      services: true,
      restaurantOfferings: true,
      toursEventsPoi: true,
      hotelGuide: true,
      news: true,

      // Inventory Management - Level 2
      inventoryRequests: true,
      serviceInventoryLogs: true,
      restaurantInventoryLogs: true,

      // Hotel Guests - Level 2
      manageHotelGuests: true,
      manageHotelBookings: true,
      manageHotelGuestCheckInPreferences: true,
      hotelGuestCheckInRequests: true,
      hotelGuestCheckInRequestsLogs: true,
      hotelGuestPermissions: true,

      // Restaurants - Level 2
      manageRestaurantOrders: true,
      manageRestaurantGuests: true,

      // Settings - Level 2
      organization: true,
      homepage: true,
      languages: true,
      paymentMethods: true,
      aiAccount: true,
      manageEmployeeHandbooks: true,
    },
    [SubscriptionType.RESTAURANT]: {
      // Level 1
      dashboard: true,
      offerings: true,
      inventoryManagement: true,
      hotelGuests: true,
      restaurants: true,
      settings: true,
      messaging:true,

      // Messaging - Level 2
      chats: true,
      chatbotConversation: false,
      reportedMessageRequests: true,
      reportedMessageLogs: true,

      // Offerings - Level 2
      rooms: false,
      services: false,
      restaurantOfferings: true,
      toursEventsPoi: false,
      hotelGuide: false,
      news: true,

      // Inventory Management - Level 2
      inventoryRequests: true,
      serviceInventoryLogs: false,
      restaurantInventoryLogs: true,

      // Hotel Guests - Level 2
      manageHotelGuests: false,
      manageHotelBookings: false,
      manageHotelGuestCheckInPreferences: false,
      hotelGuestCheckInRequests: false,
      hotelGuestCheckInRequestsLogs: false,
      hotelGuestPermissions: false,

      // Restaurants - Level 2
      manageRestaurantOrders: true,
      manageRestaurantGuests: true,

      // Settings - Level 2
      organization: true,
      homepage: true,
      languages: true,
      paymentMethods: true,
      aiAccount: false,
      manageEmployeeHandbooks: false,
    },
    [SubscriptionType.TASK_MANAGEMENT]: {
      // Level 1
      dashboard: true,
      offerings: true,
      inventoryManagement: false,
      hotelGuests: false,
      restaurants: false,
      settings: true,
      messaging: true,

      // Messaging - Level 2
      chats: true,
      chatbotConversation: true,
      reportedMessageRequests: true,
      reportedMessageLogs: true,

      // Offerings - Level 2
      rooms: false,
      services: false,
      restaurantOfferings: false,
      toursEventsPoi: false,
      hotelGuide: false,
      news: true,

      // Inventory Management - Level 2
      inventoryRequests: false,
      serviceInventoryLogs: false,
      restaurantInventoryLogs: false,

      // Hotel Guests - Level 2
      manageHotelGuests: false,
      manageHotelBookings: false,
      manageHotelGuestCheckInPreferences: false,
      hotelGuestCheckInRequests: false,
      hotelGuestCheckInRequestsLogs: false,
      hotelGuestPermissions: false,

      // Restaurants - Level 2
      manageRestaurantOrders: false,
      manageRestaurantGuests: false,

      // Settings - Level 2
      organization: true,
      homepage: true,
      languages: true,
      paymentMethods: true,
      aiAccount: true,
      manageEmployeeHandbooks: true,
    },
  };

  setSubscriptionType(type: string): void {
    this.subscriptionType.next(type);
    localStorage.setItem('subscriptionType', type);
  }

  getSubscriptionType(): string {
    return (
      this.subscriptionType.getValue() ||
      localStorage.getItem('subscriptionType') ||
      ''
    );
  }

  hasFeatureAccess(feature: keyof FeatureAccess): boolean {
    const type = this.getSubscriptionType();
    return this.featureMatrix[type]?.[feature] ?? false;
  }

  getFeatureAccess(): FeatureAccess | null {
    const type = this.getSubscriptionType();
    return this.featureMatrix[type] || null;
  }

  isHotelSubscription(): boolean {
    return this.getSubscriptionType() === SubscriptionType.HOTEL;
  }

  isRestaurantSubscription(): boolean {
    return this.getSubscriptionType() === SubscriptionType.RESTAURANT;
  }

  isTaskManagementSubscription(): boolean {
    return this.getSubscriptionType() === SubscriptionType.TASK_MANAGEMENT;
  }
}
